import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getTenant, updateTenant, uploadSignature, deleteSignature } from '../../store/actions/tenant';
import TenantMembers from './TenantMembers';
import InviteUser from '../invitation/InviteUser';

import { useFormik } from 'formik';
import { validation, formValues } from './validationSchema';
import { Button, Form, Row, Col, Tab, Nav } from 'react-bootstrap';

import { errorHelper, Loader } from '../../utils/tools';

const Settings = () => {

    const currentTenant = useSelector(state => state.tenant.current);
    const loading = useSelector(state => state.tenant.loading);
    const branchId = useSelector(state => state.branch.current);
    const isOwner = useSelector(state => state.users.data?.isOwner);
    const dispatch = useDispatch();

    const fileInputRef = useRef(null);
    const [signaturePreviewUrl, setSignaturePreviewUrl] = useState(null);
    const hasSignature = !!currentTenant?.signature?.filename;

    useEffect(() => {
        if (!hasSignature) {
            setSignaturePreviewUrl(null);
            return;
        }
        let objectUrl = null;
        const token = localStorage.getItem('token');
        fetch('/api/tenant/signature', { headers: { Authorization: `Bearer ${token}` } })
            .then(res => res.blob())
            .then(blob => {
                objectUrl = URL.createObjectURL(blob);
                setSignaturePreviewUrl(objectUrl);
            })
            .catch(() => setSignaturePreviewUrl(null));
        return () => {
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [hasSignature, currentTenant?.signature?.filename]);

    const handleSignatureFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        dispatch(uploadSignature(file));
        e.target.value = '';
    };

    const handleSignatureDelete = () => {
        dispatch(deleteSignature());
    };

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            ...formValues,
            companyName: currentTenant?.companyName || '',
            contact: currentTenant?.contact || '',
            email: currentTenant?.email || '',
            responsiblePerson: currentTenant?.responsiblePerson || '',
            password: '',
            address: {
                street: currentTenant?.address?.street || '',
                registrationNumber: currentTenant?.address?.registrationNumber || '',
                buildingNumber: currentTenant?.address?.buildingNumber || '',
                zip: currentTenant?.address?.zip || '',
                city: currentTenant?.address?.city || '',
                country: currentTenant?.address?.country || ''
            }
        },
        validationSchema: validation(currentTenant?.email),
        onSubmit: (values) => {
            values.branchId = branchId;
            dispatch(updateTenant(values));
        }
    });

    const emailChanged = !!currentTenant?.email && formik.values.email !== currentTenant?.email;

    if (loading) return <Loader />;

    return (
        <div className="content p-4">
            <Tab.Container defaultActiveKey="info">
                <Nav variant="tabs" className="mb-4">
                    <Nav.Item>
                        <Nav.Link eventKey="info">Údaje o subjekte</Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link eventKey="members">Členovia</Nav.Link>
                    </Nav.Item>
                    {isOwner && (
                        <Nav.Item>
                            <Nav.Link eventKey="invite">Pozvánky</Nav.Link>
                        </Nav.Item>
                    )}
                </Nav>

                <Tab.Content>
                    <Tab.Pane eventKey="info">
                        {!isOwner && (
                            <p className="text-muted mb-3">Údaje o subjekte môže upravovať iba vlastník subjektu.</p>
                        )}
                        <Form onSubmit={formik.handleSubmit} style={{ display: 'flex', flexDirection: 'column' }}>
                            <fieldset disabled={!isOwner} style={{ display: 'flex', flexDirection: 'column' }}>
                            <Row>
                                <Col>
                                    <Form.Group className="mb-3" controlId="formBasicCompanyName">
                                        <Form.Label>Názov*</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="Zadajte názov:"
                                            {...formik.getFieldProps('companyName')}
                                            isInvalid={errorHelper(formik, 'companyName').error}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {errorHelper(formik, 'companyName').helperText}
                                        </Form.Control.Feedback>
                                    </Form.Group>

                                    <Form.Group className="mb-3" controlId="formBasicContact">
                                        <Form.Label>Mobil* (+421910123456)</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="Zadajte mobil:"
                                            {...formik.getFieldProps('contact')}
                                            isInvalid={errorHelper(formik, 'contact').error}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {errorHelper(formik, 'contact').helperText}
                                        </Form.Control.Feedback>
                                    </Form.Group>

                                    <Form.Group className="mb-3" controlId="formBasicEmail">
                                        <Form.Label>E-mail*</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="Zadajte e-mail:"
                                            {...formik.getFieldProps('email')}
                                            isInvalid={errorHelper(formik, 'email').error}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {errorHelper(formik, 'email').helperText}
                                        </Form.Control.Feedback>
                                    </Form.Group>

                                    {emailChanged && (
                                        <Form.Group className="mb-3" controlId="formBasicPassword">
                                            <Form.Label>Heslo* (potvrdenie zmeny e-mailu)</Form.Label>
                                            <Form.Control
                                                type="password"
                                                placeholder="Zadajte vaše heslo:"
                                                {...formik.getFieldProps('password')}
                                                isInvalid={errorHelper(formik, 'password').error}
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {errorHelper(formik, 'password').helperText}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    )}

                                    <Form.Group className="mb-3" controlId="formBasicResponsiblePerson">
                                        <Form.Label>Zodpovedná osoba*</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="Zadajte zodpovednú osobu:"
                                            {...formik.getFieldProps('responsiblePerson')}
                                            isInvalid={errorHelper(formik, 'responsiblePerson').error}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {errorHelper(formik, 'responsiblePerson').helperText}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Col>

                                <Col>
                                    <Row>
                                        <Col md={6}>
                                            <Form.Group className="mb-3" controlId="formBasicStreet">
                                                <Form.Label>Ulica*</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Zadajte ulicu:"
                                                    {...formik.getFieldProps('address.street')}
                                                    isInvalid={formik.touched.address?.street && formik.errors.address?.street}
                                                />
                                                <Form.Control.Feedback type="invalid">
                                                    {formik.errors.address?.street}
                                                </Form.Control.Feedback>
                                            </Form.Group>
                                        </Col>
                                        <Col md={3}>
                                            <Form.Group className="mb-3" controlId="formBasicRegistrationNumber">
                                                <Form.Label>Súpisné č.</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Napr. 123"
                                                    {...formik.getFieldProps('address.registrationNumber')}
                                                    isInvalid={formik.touched.address?.registrationNumber && formik.errors.address?.registrationNumber}
                                                />
                                                <Form.Control.Feedback type="invalid">
                                                    {formik.errors.address?.registrationNumber}
                                                </Form.Control.Feedback>
                                            </Form.Group>
                                        </Col>
                                        <Col md={3}>
                                            <Form.Group className="mb-3" controlId="formBasicBuildingNumber">
                                                <Form.Label>Orient. č.</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Napr. 4A"
                                                    {...formik.getFieldProps('address.buildingNumber')}
                                                    isInvalid={formik.touched.address?.buildingNumber && formik.errors.address?.buildingNumber}
                                                />
                                                <Form.Control.Feedback type="invalid">
                                                    {formik.errors.address?.buildingNumber}
                                                </Form.Control.Feedback>
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    <Form.Group className="mb-3" controlId="formBasicCity">
                                        <Form.Label>Mesto*</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="Zadajte mesto:"
                                            {...formik.getFieldProps('address.city')}
                                            isInvalid={formik.touched.address?.city && formik.errors.address?.city}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {formik.errors.address?.city}
                                        </Form.Control.Feedback>
                                    </Form.Group>

                                    <Form.Group className="mb-3" controlId="formBasicZip">
                                        <Form.Label>PSČ*</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="Zadajte PSČ:"
                                            {...formik.getFieldProps('address.zip')}
                                            isInvalid={formik.touched.address?.zip && formik.errors.address?.zip}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {formik.errors.address?.zip}
                                        </Form.Control.Feedback>
                                    </Form.Group>

                                    <Form.Group className="mb-3" controlId="formBasicCountry">
                                        <Form.Label>Krajina*</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="Zadajte krajinu:"
                                            {...formik.getFieldProps('address.country')}
                                            isInvalid={formik.touched.address?.country && formik.errors.address?.country}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {formik.errors.address?.country}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                            </Row>
                            </fieldset>

                            {isOwner && (
                                <Row className="mt-4">
                                    <Col xs={12}>
                                        <Form.Label className="fw-semibold">Podpis</Form.Label>
                                        <div className="d-flex align-items-center gap-3 flex-wrap">
                                            {hasSignature ? (
                                                <div className="d-flex align-items-center gap-3">
                                                    {signaturePreviewUrl ? (
                                                        <img
                                                            src={signaturePreviewUrl}
                                                            alt="Podpis"
                                                            style={{ maxHeight: 80, maxWidth: 240, border: '1px solid #dee2e6', borderRadius: 4, background: '#fff', padding: 4 }}
                                                        />
                                                    ) : (
                                                        <span className="text-muted">Podpis nahraný</span>
                                                    )}
                                                    <Button variant="outline-danger" size="sm" onClick={handleSignatureDelete}>
                                                        Odstrániť podpis
                                                    </Button>
                                                </div>
                                            ) : (
                                                <span className="text-muted">Žiadny podpis</span>
                                            )}
                                            <Button variant="outline-secondary" size="sm" onClick={() => fileInputRef.current?.click()}>
                                                {hasSignature ? 'Nahradiť podpis' : 'Nahrať podpis'}
                                            </Button>
                                            <input
                                                ref={fileInputRef}
                                                type="file"
                                                accept="image/jpeg,image/png,image/webp"
                                                style={{ display: 'none' }}
                                                onChange={handleSignatureFileChange}
                                            />
                                        </div>
                                        <Form.Text className="text-muted">JPG, PNG alebo WebP, max. 5 MB.</Form.Text>
                                    </Col>
                                </Row>
                            )}

                            {isOwner && (
                                <Row className="mt-3">
                                    <Col xs={12} sm={6} md={4}>
                                        <Button className="button-custom-large" variant="primary" type="submit" size="lg">
                                            Upraviť subjekt
                                        </Button>
                                    </Col>
                                </Row>
                            )}
                        </Form>
                    </Tab.Pane>

                    <Tab.Pane eventKey="members">
                        <TenantMembers />
                    </Tab.Pane>

                    {isOwner && (
                        <Tab.Pane eventKey="invite">
                            <InviteUser />
                        </Tab.Pane>
                    )}
                </Tab.Content>
            </Tab.Container>
        </div>
    );
}

export default Settings;
